import React, { useEffect, useState, useRef } from "react";
import { HexColorPicker } from "react-colorful";
import {
  useCreateTeamMutation,
  useGetTeamQuery,
} from "../../redux/features/teams/teamsApi";
import lightenDarkenColor from "../../utils/lightenDarkenColor";
import { toast } from "react-toastify";
import useAuth from "../../hooks/useAuth";
import { ThreeCircles } from "react-loader-spinner";

function TeamForm({ handleModal, modalRef }) {
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [teamColor, setTeamColor] = useState("#7c3aed");
  const [teamCheck, setTeamCheck] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [teamDescription, setTeamDescription] = useState("");
  const [error, setError] = useState(null);
  const convertedTeamName = teamName.toUpperCase();

  const { user } = useAuth();
  const [createTeam, { isLoading }] = useCreateTeamMutation();
  const { data, error: responseError } = useGetTeamQuery(
    { convertedTeamName },
    {
      skip: !teamCheck,
    }
  );

  const formReset = () => {
    setError(null);
    setTeamDescription("");
    setTeamName("");
    setTeamColor("#7c3aed");
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!error && teamName && teamColor && teamDescription) {
      try {
        formReset();
        await createTeam({
          teamName: teamName.toUpperCase(),
          teamDescription,
          teamColor,
          teamCreatror: user,
          teamMembers: [user],
          timeStamp: new Date().getTime(),
        }).unwrap();
        toast.success("Team has been created successfully", {
          autoClose: 2000,
        });
      } catch (error) {
        setError(error);
      }
    }
    if (!teamName && !teamDescription) {
      setError("Please fill all the field");
    }
  };

  const checkTeamExistance = (e) => {
    if (teamName.length > 0) {
      setTeamCheck(true);
    }
  };

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (responseError) {
      setError("Network or server error");
    }
    if (data?.length > 0) {
      setError("A team with same name already exists");
    }
  }, [responseError, data]);

  const handleColorPicker = () => {
    setColorPickerOpen(!colorPickerOpen);
  };

  return (
    <>
      <h1 className="mt-20 mb-8 text-3xl font-extrabold text-violet-600">
        Create a team
      </h1>
      <form className="flex flex-col gap-[12px]">
        <div>
          <input
            id="team-name"
            onChange={(e) => {
              setError(null);
              setTeamCheck(false);
              setTeamName(e.target.value);
            }}
            onBlur={checkTeamExistance}
            name="team-name"
            value={teamName}
            type="text"
            required
            className=" relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-violet-500 focus:border-violet-500 focus:z-10 sm:text-sm"
            placeholder="Team Name"
          />
        </div>
        <div>
          <textarea
            id="team-description"
            cols={50}
            rows={4}
            name="team-description"
            type="text"
            value={teamDescription}
            required
            className=" relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-violet-500 focus:border-violet-500 focus:z-10 sm:text-sm"
            placeholder="Team Description"
            onChange={(e) => setTeamDescription(e.target.value)}
          />
        </div>
        <div
          className="px-1 py-1 rounded-md bg-white cursor-pointer active:scale-[97%] ease-linear duration-75"
          onClick={handleColorPicker}
        >
          <p
            style={{
              background: teamColor,
              color: lightenDarkenColor(teamColor, -80),
            }}
            className={`px-2 py-1 rounded-md `}
          >
            {colorPickerOpen ? "Set Team Color" : "Pick a Team Color"}
          </p>
        </div>

        {colorPickerOpen && (
          <div
            className="relative animate__animated animate__flipInX"
            id="colorPicker"
          >
            <HexColorPicker
              style={{ width: "100%", height: "150px" }}
              className
              color={teamColor}
              onChange={setTeamColor}
            />
          </div>
        )}

        <div className="relative flex justify-between items-center gap-4">
          <button
            disabled={
              teamName.length === 0 || teamDescription.length === 0 || error
            }
            className={` ${
              isLoading
                ? " bg - black / 25"
                : " bg-violet-600 hover:bg-violet-700 "
            }    disabled:bg-gray-200 disabled:text-gray-400 disabled:scale-100 disabled:cursor-not-allowed w-1/2 px-3 py-2 rounded-md font-semibold text-violet-200 active:scale-[97%] ease-linear duration-75 `}
            onClick={handleCreateTeam}
          >
            Create
          </button>
          <div className="absolute top-[50%] -translate-y-[50%] -translate-x-[25%] left-[21.5%] ">
            <ThreeCircles
              height="30"
              width="30"
              color="#4C51BF"
              wrapperStyle={{}}
              wrapperclassName=""
              visible={isLoading}
              ariaLabel="three-circles-rotating"
              outerCircleColor=""
              innerCircleColor=""
              middleCircleColor=""
            />
          </div>

          <button
            className="w-1/2 bg-red-600 hover:bg-red-700 px-3 py-2 rounded-md font-semibold text-red-200 active:scale-[97%] ease-linear duration-75"
            onClick={(e) => {
              e.preventDefault();
              modalRef?.current?.classList.add("animate__zoomOut");
              modalRef?.current?.addEventListener("animationend", (event) => {
                handleModal();
              });
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </>
  );
}

export default TeamForm;
